import { Slot } from "@radix-ui/themes";
import cx from "classnames";
import { type ComponentProps, forwardRef, type ReactNode, useRef } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "./SidebarTooltip";
import { useSidebar } from "./useSidebar";

export const SidebarMenuButton = forwardRef<
  HTMLButtonElement,
  ComponentProps<"button"> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: ReactNode;
    tooltipClassName?: string;
    tooltipSide?: "right" | "left";
  }
>(
  (
    {
      asChild = false,
      isActive = false,
      tooltip,
      className,
      tooltipClassName,
      tooltipSide = "right",
      onClick,
      ...props
    },
    ref,
  ) => {
    // Only use a slot if you know what you're doing and accept the styling
    // consequences.
    const Comp = asChild ? Slot : "button";
    const { isMobile, state } = useSidebar();

    // Keep a handle on the rendered trigger so the collapsed hover label can
    // forward its clicks here (see TooltipContent.onClick below), while still
    // honouring any ref the caller passed.
    const innerRef = useRef<HTMLButtonElement | null>(null);
    const assignRef = (node: HTMLButtonElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLButtonElement | null>).current =
          node;
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Clear any hover states to prevent tooltip from staying visible during navigation
      const target = e.currentTarget as HTMLElement;
      if (target) {
        target.blur();
        // Also trigger mouseleave to clear hover state
        target.dispatchEvent(new MouseEvent("mouseleave", { bubbles: true }));
      }

      // Call the original onClick if provided
      onClick?.(e);
    };

    const button = (
      <Comp
        ref={assignRef}
        data-state={state}
        data-sidebar="menu-button"
        data-active={isActive}
        className={cx(
          {
            "peer/menu-item flex w-full items-center gap-2 overflow-hidden whitespace-nowrap rounded-md bg-transparent px-2 py-[7px] outline-none transition-[width,height,padding]":
              !asChild,
          },
          className,
        )}
        onClick={handleClick}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side={tooltipSide}
          align="center"
          sideOffset={0}
          hidden={state !== "collapsed" || isMobile}
          data-testid="sidebar-menu-button-tooltip"
          className={cx(tooltipClassName, "-ml-10 cursor-pointer")}
          // The collapsed pill visually extends out of the icon-only button, so
          // a click ANYWHERE on the label (including the protruding text) should
          // do exactly what the button does. The content sits on top of / beyond
          // the trigger, so forward its click to the underlying button rather
          // than relying on click-through (which left the protruding text inert).
          onClick={(e) => {
            e.preventDefault();
            innerRef.current?.click();
          }}
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  },
);

SidebarMenuButton.displayName = "SidebarMenuButton";
